import React from 'react';

/**
 * ローディングスピナーのプロパティ
 */
export interface LoadingSpinnerProps {
  /**
   * スピナーのサイズ
   * @default 'md'
   */
  readonly size?: 'sm' | 'md' | 'lg';

  /**
   * スピナーの色
   * @default 'white'
   */
  readonly color?: 'white' | 'indigo' | 'gray';

  /**
   * スクリーンリーダー用テキスト
   * @default '読み込み中...'
   */
  readonly srText?: string;

  /**
   * 追加の CSS クラス
   */
  readonly className?: string;
}

/**
 * サイズに応じた CSS クラスを返す
 */
function getSizeClass(size: 'sm' | 'md' | 'lg'): string {
  const sizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-8 w-8',
  };
  return sizes[size];
}

/**
 * 色に応じた CSS クラスを返す
 */
function getColorClass(color: 'white' | 'indigo' | 'gray'): string {
  const colors = {
    white: 'text-white',
    indigo: 'text-indigo-600',
    gray: 'text-gray-600',
  };
  return colors[color];
}

/**
 * ローディングスピナーコンポーネント
 *
 * アニメーション付きのローディングインジケーター。
 * サイズと色をカスタマイズ可能。
 *
 * @example
 * ```tsx
 * // デフォルト（中サイズ、白色）
 * <LoadingSpinner />
 *
 * // 大サイズ、indigo 色
 * <LoadingSpinner size="lg" color="indigo" />
 *
 * // カスタムスクリーンリーダーテキスト
 * <LoadingSpinner srText="サインイン処理中..." />
 * ```
 */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'white',
  srText = '読み込み中...',
  className = '',
}) => {
  const sizeClass = getSizeClass(size);
  const colorClass = getColorClass(color);

  return (
    <>
      <svg
        className={`animate-spin ${sizeClass} ${colorClass} ${className}`}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <span className="sr-only">{srText}</span>
    </>
  );
};
